import React, { useState, useEffect } from 'react';
import { backgroundCheckAPI } from '../../services/backgroundCheckAPI';
import { formatAPIError } from '../../utils/validation';

const RequestBackgroundCheckModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Enter Details, 2: Select Agency & Scope

  // Step 1: Candidate Details
  const [candidateEmail, setCandidateEmail] = useState('');
  const [candidateUserId, setCandidateUserId] = useState(''); // OR profileId

  // Step 2: Agency & Scope Selection
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [scope, setScope] = useState(['criminal']); // Criminal is required
  const [loadingAgencies, setLoadingAgencies] = useState(false);

  const scopeOptions = [
    { id: 'criminal', label: 'Criminal History Check', required: true, description: 'Required by law' },
    { id: 'employment', label: 'Employment Verification', required: false, description: 'Verify past employment' },
    { id: 'education', label: 'Education Verification', required: false, description: 'Verify education credentials' },
    { id: 'credit', label: 'Credit Check', required: false, description: 'Financial background check' },
    { id: 'drug', label: 'Drug Screening', required: false, description: 'Substance abuse testing' },
    { id: 'reference', label: 'Reference Check', required: false, description: 'Contact professional references' },
  ];

  // Load agencies when moving to step 2
  useEffect(() => {
  if (step === 2 && agencies.length === 0) {
    loadAgencies();
  }
}, [step, agencies.length]);


  const loadAgencies = async () => {
    try {
      setLoadingAgencies(true);
      console.log('🔍 Loading verified agencies...');
      
      const agenciesList = await backgroundCheckAPI.getVerifiedAgencies();

console.log("Agency sample:", agenciesList?.[0]);

setAgencies(agenciesList || []);


    } catch (error) {
      console.error('❌ Failed to load agencies:', error);
      setError(formatAPIError(error) || 'Failed to load agencies');
    } finally {
      setLoadingAgencies(false);
    }
  };

  const handleProceedToStep2 = (e) => {
    e.preventDefault();
    
    if (!candidateEmail.trim() && !candidateUserId.trim()) {
      setError('Please enter candidate email or user ID');
      return;
    }

    // Simple email validation
    if (candidateEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(candidateEmail)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setError('');
    setStep(2);
  };

  const handleScopeToggle = (scopeId) => {
    if (scopeId === 'criminal') return; // Criminal is mandatory

    setScope(prev => 
      prev.includes(scopeId)
        ? prev.filter(s => s !== scopeId)
        : [...prev, scopeId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAgency) {
      setError('Please select an agency');
      return;
    }

    if (scope.length === 0) {
      setError('Please select at least one verification scope');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // ✅ Build request payload based on what employer provided
      const requestPayload = {
        agencyId: selectedAgency,
        scope: scope,
        candidateUserId: candidateUserId.trim(),
      };

      // ✅ Use candidateUserId if provided, otherwise use email as fallback
      if (candidateUserId.trim()) {
        requestPayload.candidateUserId = candidateUserId.trim();
      } else if (candidateEmail.trim()) {
        // Send candidate email - backend will look up the user
        requestPayload.candidateEmail = candidateEmail.trim();
      }

      console.log('📤 Submitting background check request:', requestPayload);

      const response = await backgroundCheckAPI.assignBackgroundCheck(requestPayload);

      console.log('✅ Background check request submitted successfully:', response);

      // Success!
      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      console.error('❌ Failed to assign background check:', error);
      const errorMsg = formatAPIError(error);
      
      // Handle specific error cases
      if (errorMsg.includes('Candidate not found') || errorMsg.includes('User not found')) {
        setError('Candidate not found. Please verify the email address or user ID.');
      } else if (errorMsg.includes('already has an active')) {
        setError('This candidate already has an active background check request.');
      } else if (errorMsg.includes('No active disclaimer')) {
        setError('System configuration error: No active disclaimer found. Please contact support.');
      } else {
        setError(errorMsg || 'Failed to assign background check');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setCandidateEmail('');
    setCandidateUserId('');
    setSelectedAgency('');
    setScope(['criminal']);
    setError('');
    onClose();
  };

  const handleBack = () => {
    setStep(1);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="dashboard-modal-overlay" onClick={handleClose}>
      <div className="dashboard-modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        <div className="dashboard-modal-header">
          <h2>Request Background Verification</h2>
          <button className="dashboard-close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="dashboard-modal-body">
          {error && (
            <div style={{ 
              padding: '12px 16px', 
              background: '#fee2e2', 
              border: '1px solid #fecaca',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* STEP 1: Enter Candidate Details */}
          {step === 1 && (
            <div>
              <div style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
                  Enter the candidate's email address or user ID to request background verification.
                </p>

                <form onSubmit={handleProceedToStep2}>
                  <div className="form-group">
                    <label className="form-label">Candidate Email *</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="candidate@example.com"
                      value={candidateEmail}
                      onChange={(e) => {
                        setCandidateEmail(e.target.value);
                        setCandidateUserId(''); // Clear userId if email is entered
                      }}
                    />
                    <small style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', display: 'block' }}>
                      The candidate's registered email address
                    </small>
                  </div>

                  <div style={{ 
                    textAlign: 'center', 
                    margin: '1rem 0', 
                    color: '#999',
                    fontSize: '0.875rem',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: 0, 
                      right: 0, 
                      height: '1px', 
                      background: '#e5e7eb' 
                    }}></div>
                    <span style={{ 
                      position: 'relative', 
                      background: 'white', 
                      padding: '0 1rem' 
                    }}>
                      OR
                    </span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Candidate User ID</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., 7122f55c-27fc-4fcf-890d-89dba1ec03b3"
                      value={candidateUserId}
                      onChange={(e) => {
                        setCandidateUserId(e.target.value);
                        setCandidateEmail(''); // Clear email if userId is entered
                      }}
                    />
                    <small style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem', display: 'block' }}>
                      Optional: If you have the candidate's system user ID
                    </small>
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary"
                    style={{ width: '100%', marginTop: '1rem' }}
                    disabled={!candidateEmail.trim() && !candidateUserId.trim()}
                  >
                    Continue →
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* STEP 2: Select Agency and Scope */}
          {step === 2 && (
            <form onSubmit={handleSubmit}>
              {/* Candidate Info Display */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
                  <strong>Candidate:</strong> {candidateEmail || `User ID: ${candidateUserId}`}
                </p>
              </div>

              {/* Agency Selection */}
              <div className="form-group">
                <label className="form-label">Select Verification Agency *</label>
                {loadingAgencies ? (
                  <p style={{ fontSize: '0.875rem', color: '#666' }}>Loading agencies...</p>
                ) : agencies.length > 0 ? (
                  <select
                    className="form-control"
                    value={selectedAgency}
                    onChange={(e) => setSelectedAgency(e.target.value)}
                    required
                  >
                    <option value="">Choose an agency...</option>
                    {agencies.map(agency => (
                      <option key={agency.id} value={agency.id}>
                        {agency.email || `${agency.firstName} ${agency.lastName}`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: '8px' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#dc2626' }}>
                      ⚠️ No verified agencies available. Please contact support to register agencies.
                    </p>
                  </div>
                )}
              </div>

              {/* Scope Selection */}
              <div className="form-group">
                <label className="form-label">Verification Scope *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {scopeOptions.map(option => (
                    <label 
                      key={option.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '0.75rem',
                        border: scope.includes(option.id) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '6px',
                        cursor: option.required ? 'not-allowed' : 'pointer',
                        background: scope.includes(option.id) ? '#eff6ff' : 'transparent',
                        opacity: option.required ? 0.7 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={scope.includes(option.id)}
                        onChange={() => handleScopeToggle(option.id)}
                        disabled={option.required}
                        style={{ marginTop: '0.25rem', marginRight: '0.75rem' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                          {option.label}
                          {option.required && (
                            <span style={{ marginLeft: '0.5rem', color: '#dc2626', fontSize: '0.75rem' }}>
                              (Required)
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {option.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button 
                  type="button" 
                  onClick={handleBack}
                  className="btn-secondary"
                >
                  ← Back
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={loading || agencies.length === 0}
                >
                  {loading ? 'Submitting...' : '✓ Submit Request'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestBackgroundCheckModal;
